import React from 'react';
import {ActionIcon, Badge} from '@mantine/core';
import {Collapse} from 'src/design/atoms/Collapse';
import {Flex} from 'src/design/atoms/Flex';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconChevronDown, IconChevronRight} from '@tabler/icons-react';

export interface WorkflowItemCardProps {
  index: number;
  title?: React.ReactNode;
  summary?: string;
  actions?: React.ReactNode;
  variant?: 'default' | 'container';
  expanded?: boolean;
  onToggleExpanded?: () => void;
  children: React.ReactNode;
}

export const WorkflowItemCard: React.FC<WorkflowItemCardProps> = ({
  index,
  title,
  summary,
  actions,
  variant = 'default',
  expanded = true,
  onToggleExpanded,
  children,
}) => {
  const isCollapsible = onToggleExpanded !== undefined;

  return (
    <Paper
      withBorder
      shadow={variant === 'container' ? undefined : 'xs'}
      bg={variant === 'container' ? 'gray.0' : undefined}
      p="md">
      <Stack gap="md">
        <Flex
          justify="space-between"
          align="center"
          style={isCollapsible ? {cursor: 'pointer'} : undefined}
          onClick={isCollapsible ? onToggleExpanded : undefined}>
          <Group gap="sm">
            {isCollapsible && (
              <ActionIcon variant="subtle" size="md" aria-label="Toggle expand">
                {expanded ? (
                  <IconChevronDown size={18} />
                ) : (
                  <IconChevronRight size={18} />
                )}
              </ActionIcon>
            )}
            <Badge variant="filled" color="gray" size="lg" circle>
              {index + 1}
            </Badge>
            {title}
            {!expanded && summary && (
              <Text size="sm" c="dimmed" lineClamp={1}>
                — {summary}
              </Text>
            )}
          </Group>
          {actions && (
            <Group gap="xs" onClick={e => e.stopPropagation()}>
              {actions}
            </Group>
          )}
        </Flex>

        <Collapse in={expanded}>
          <Stack gap="md">{children}</Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};
